/**
 * Custom hook for generating language URLs for job detail pages.
 * Delegates to canonical URL helpers so URLs are identical across JDP head tags, SRP links, and sitemap generation.
 */

import { useMemo } from 'react';
import { BASE_URL } from '../../../../../utils/constants/url-constants';
import type { FinalJob } from '../../../../../lambda/types';
import { generateCanonicalUrls, generateJobUrlForLocale } from '../../../../../utils/seo/generate-canonical-urls';

export interface LanguageUrls {
  current: string;
  alternates: Array<{ href: string; hrefLang: string }>;
  all: Array<{ locale: 'en' | 'fi' | 'sv'; url: string }>;
}

export interface UseLanguageUrlsOptions {
  canonicalSource: FinalJob | null | undefined;
  canonicalLocale: 'en' | 'fi' | 'sv';
}

const SUPPORTED_LOCALES = ['en', 'fi', 'sv'] as const;
type Locale = typeof SUPPORTED_LOCALES[number];

/**
 * Generate URLs for all languages using current host.
 */
export function useLanguageUrls({ canonicalSource, canonicalLocale }: UseLanguageUrlsOptions): LanguageUrls | null {
  return useMemo(() => {
    if (!canonicalSource) return null;

    const allUrls = SUPPORTED_LOCALES.map(locale => ({
      locale,
      url: generateJobUrlForLocale(canonicalSource, locale).replace(BASE_URL, ''),
    }));

    const getAbsolute = (relative: string) =>
      typeof window !== 'undefined'
        ? `${window.location.origin}${relative}`
        : `${BASE_URL}${relative}`;

    const canonicalData = generateCanonicalUrls(canonicalSource, canonicalLocale);
    const currentRelative = canonicalData.canonicalUrl.replace(BASE_URL, '');

    return {
      current: getAbsolute(currentRelative),
      alternates: canonicalData.alternates.map((alt) => ({
        href: getAbsolute(alt.href.replace(BASE_URL, '')),
        hrefLang: alt.hrefLang,
      })),
      all: allUrls,
    };
  }, [canonicalSource, canonicalLocale]);
}
